import i18n from "@/i18n";
import { createRouter, createWebHistory } from "vue-router";

import HomeView from "@/Views/HomeView.vue";
import ProductsPage from "@/Views/ProductsPage.vue";
import AboutPage from "@/Views/AboutPage.vue";
import BookPage from "@/Views/BookPage.vue";
import EDMPage from "@/Views/EDMPage.vue";

// Blog
import BlogList from "@/Views/BlogList.vue";
import BlogDetail from "@/Views/BlogDetail.vue";

const defaultTitle = "聯洋文化｜美國同步教材・雙語英文教材平台";

const defaultDescription =
  "聯洋文化為 HMH 與 Vista Higher Learning 台灣代理，提供 K–12 美國同步教材、Phonics 發音教材、ELA、英文讀本、AP、IB、數學與科學教材，適用雙語學校、國際學校與英文補教機構。";

const routes = [
  {
    path: "/",
    redirect: { name: "Home" },
  },

  {
    path: "/",
    name: "Home",
    component: HomeView,
    meta: {
      title: defaultTitle,
      description: defaultDescription,
    },
  },

  {
    path: "/AboutPage",
    name: "About",
    component: AboutPage,
    meta: {
      title: "關於聯洋文化｜美國同步教材代理與雙語教育資源",
      description:
        "聯洋文化長期引進 HMH、Vista Higher Learning 等國際教育出版資源，提供台灣雙語學校、國際學校與英文補教機構 K–12 教材與教學支援。",
    },
  },

  {
    path: "/ProductsPage/:IconButtonId",
    name: "ProductsPage",
    component: ProductsPage,
    props: true,
    meta: {
      description:
        "瀏覽聯洋文化 美國同步教材分類，包含 Phonics 發音教材、英文閱讀教材、ELA、數學教材、科學教材、AP 與 IB 教材，適合雙語學校與國際教育課程。",
    },
  },

  {
    path: "/BookPage/:IconButtonId/:BookPageId",
    name: "BookPage",
    component: BookPage,
    props: true,
    meta: {
      title: "英文教材介紹｜美國同步教材・雙語學校教材推薦",
      description:
        "查看聯洋文化精選英文教材與美國同步教材，包含教材特色、適用年級、學科分類與教學應用，適合雙語學校、補習班與親子英文學習。",
    },
  },

  {
    path: "/EDMPage",
    name: "EDM",
    component: EDMPage,
    props: true,
    meta: {
      title: "聯洋文化活動與教材資訊｜英文教材推廣與教育資源",
      description:
        "查看聯洋文化最新教材活動、英文教材推廣資訊、教育資源與雙語教學內容，掌握 HMH、Vista 與 K–12 美國同步教材消息。",
    },
  },

  // =========================
  // Blog
  // =========================

  {
    path: "/Blog",
    name: "Blog",
    component: BlogList,
    meta: {
      title: "教育專欄｜英文教材教學文章與雙語教育資源",
      description:
        "聯洋文化教育專欄，分享 Phonics 自然發音、英文閱讀、雙語教學、SDGs 繪本與 K–12 美國同步教材應用。",
    },
  },

  {
    path: "/Blog/:id",
    name: "BlogDetail",
    component: BlogDetail,
    props: true,
    meta: {
      title: "英文教材文章｜聯洋文化教育專欄",
      description:
        "閱讀聯洋文化教育專欄文章，探索英文教材、閱讀素養、雙語教育與教學資源。",
    },
  },

  // =========================
  // 教師節活動追蹤短網址
  // =========================

  {
    path: "/teachers-day-facebook",
    redirect: {
      path: "/shop",
      query: {
        utm_source: "facebook",
        utm_medium: "social",
        utm_campaign: "teachers_day_2026",
        utm_content: "fb_post",
      },
    },
  },

  {
    path: "/teachers-day-line",
    redirect: {
      path: "/shop",
      query: {
        utm_source: "line",
        utm_medium: "social",
        utm_campaign: "teachers_day_2026",
        utm_content: "line_message",
      },
    },
  },

  {
    path: "/teachers-day-email",
    redirect: {
      path: "/shop",
      query: {
        utm_source: "newsletter",
        utm_medium: "email",
        utm_campaign: "teachers_day_2026",
        utm_content: "edm_button",
      },
    },
  },

  // =========================
  // 購物公版
  // =========================

  {
    path: "/shop",
    name: "ShopPage",
    component: () => import("@/Views/ShopPage/ShopPage.vue"),
    meta: {
      title: "教材優惠活動｜聯洋文化",
      description:
        "聯洋文化精選英文教材優惠活動，提供幼兒園、國小、國中、高中英文教材與學習資源。",
    },
  },

  {
    path: "/cart",
    name: "CartPage",
    component: () => import("@/Views/ShopPage/CartPage.vue"),
    meta: {
      title: "購物車｜聯洋文化",
      description: "查看已選購的聯洋文化教材商品與訂購數量。",
    },
  },

  {
    path: "/checkout",
    name: "CheckoutPage",
    component: () => import("@/Views/ShopPage/CheckoutPage.vue"),
    meta: {
      title: "結帳｜聯洋文化",
      description:
        "聯洋文化教材訂購結帳頁面，填寫訂購資訊、配送方式與聯絡資料。",
    },
  },

  // =========================
  // Admin
  // =========================

  {
    path: "/admin/order",
    name: "AdminOrder",
    component: () => import("@/Views/AdminOrder.vue"),
    meta: {
      title: "訂單管理後台｜聯洋文化",
      description: "聯洋文化內部訂單管理後台。",
    },
  },

  {
    path: "/admin/books",
    name: "AdminBooks",
    component: () => import("@/Views/Admin/AdminBooks.vue"),
    meta: {
      title: "教材內容後台｜聯洋文化",
      description: "聯洋文化教材內容管理後台。",
    },
  },

  // =========================
// Marketing
// =========================

{
  path: "/reading-assessment",
  name: "ReadingAssessment",
  component: () =>
    import("@/Views/MarketingPage/ReadingAssessmentPage.vue"),
  meta: {
    title: "從 ESL 到 ELA｜K–6 英語閱讀能力進階指南｜聯洋文化",
    description:
      "透過互動式閱讀能力檢測，了解學生從 ESL 英語學習走向 ELA 英語語文學習的閱讀能力發展，探索 K–6 美國小學閱讀課程架構與教材資源。",
  },
},

  // =========================
  // Training
  // =========================

  {
    path: "/t-video",
    redirect: "/",
  },

  {
    path: "/training-admin",
    name: "TrainingAdmin",
    component: () => import("@/Views/Training-admin.vue"),
    meta: {
      title: "Training Admin｜聯洋文化",
      description: "聯洋文化師訓影片管理頁。",
    },
  },

  {
    path: "/t-video/:courseId",
    name: "TVideoCourse",
    component: () => import("@/Views/T-Video.vue"),
    props: true,
    meta: {
      title: "師訓影片｜聯洋文化",
      description: "聯洋文化師訓影片。",
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.afterEach((to) => {
  if (to.name === "ProductsPage") {
    const pageKey = String(to.params.IconButtonId || "");
    const titleKey = `pageTitle.${pageKey}`;
    const translatedTitle = i18n.global.t(titleKey);

    document.title =
      translatedTitle && translatedTitle !== titleKey
        ? `${translatedTitle}｜聯洋文化`
        : defaultTitle;
  } else if (to.name !== "BookPage") {
    document.title = to.meta.title || defaultTitle;
  }

  let description = document.querySelector('meta[name="description"]');

  if (!description) {
    description = document.createElement("meta");
    description.setAttribute("name", "description");
    document.head.appendChild(description);
  }

  description.setAttribute(
    "content",
    to.meta.description || defaultDescription
  );
});

export default router;